import { Injectable } from '@angular/core';
import { AntipatternStore, AppView, UserStore, ViewStore, isCurrentUserNotRestricted } from '@coach-bot/data-access/shared';
import { TranslateService } from '@ngx-translate/core';
import { AppAction, ExtensionCommunicationService } from '@shared/extension';
import { Language, LanguageData, getLanguageData } from '@shared/translate';
import { Subscription } from 'rxjs';

@Injectable()
export class AppSubscriptionsService {
  private currentLanguageSubscription: Subscription | null = null;
  private currentViewSubscription: Subscription | null = null;

  private isSubscribed: boolean = false;

  private actionHandlersMap: Map<AppAction, (event?: Event) => void> = new Map([
    [AppAction.ShowAntipatternPopup, this.showAntipatternPopup.bind(this)],
    [AppAction.ShowAntipatternPopupV2, this.showAntipatternPopupV2.bind(this)],
    [AppAction.ShowNotificationPopup, this.showNotificationPopup.bind(this)],
  ]);

  private subscriptions: Subscription[] = [];

  constructor(
    private antipatternStore: AntipatternStore,
    private extensionCommunicationService: ExtensionCommunicationService,
    private translateService: TranslateService,
    private userStore: UserStore,
    private viewStore: ViewStore
  ) {}

  subscribe() {
    if (this.isSubscribed) {
      return;
    }
    this.actionHandlersMap.forEach((handler: () => void, action: AppAction) => {
      document.addEventListener(action, handler);
    });

    this.currentLanguageSubscription = this.viewStore.currentLanguage$.subscribe((currentLanguage: Language) => {
      const languageData: LanguageData | null = getLanguageData(currentLanguage);
      if (languageData) {
        this.viewStore.setCurrencySign(languageData.currencySign);
      }
      this.translateService.use(currentLanguage);
    });

    this.currentViewSubscription = this.viewStore.currentNotificationView$.subscribe((currentView: AppView | null) => {
      const showOverlay: boolean =
        !!currentView && currentView !== AppView.Antipattern && currentView !== AppView.AntipatternV2;
      this.extensionCommunicationService.sendViewChanged(currentView, showOverlay);
    });

    this.subscriptions = [this.currentLanguageSubscription, this.currentViewSubscription];

    this.isSubscribed = true;
  }

  unsubscribe() {
    if (!this.isSubscribed) {
      return;
    }
    this.actionHandlersMap.forEach((handler: () => void, action: AppAction) => {
      document.removeEventListener(action, handler);
    });

    this.subscriptions.forEach((subscription: Subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    this.isSubscribed = false;
  }

  private showAntipatternPopup(event: Event): void {
    this.antipatternStore.setAntipatternData((event as CustomEvent).detail);
    this.viewStore.setNotificationView(AppView.Antipattern);
  }

  private showAntipatternPopupV2(event: Event): void {
    this.antipatternStore.setAntipatternData((event as CustomEvent).detail);
    this.viewStore.setNotificationView(AppView.AntipatternV2);
  }

  private async showNotificationPopup(event: Event): Promise<void> {
    const isCurrentUserNotRestrictedValue: boolean = await isCurrentUserNotRestricted(
      this.extensionCommunicationService,
      this.userStore
    );
    if (isCurrentUserNotRestrictedValue) {
      this.viewStore.setNotificationDetails((event as CustomEvent).detail);
      this.viewStore.setNotificationView(AppView.Notification);
    }
  }
}
